import { Link } from "react-router-dom";
import { Button, Card, CardContent, Badge } from "@learn-me/ui";
import { useCourses } from "@/features/courses/hooks/useCourses";
import { formatCurrency, COURSE_LEVEL_LABELS } from "@learn-me/utils";
import { ArrowRight, BookOpen, Star, Play } from "lucide-react";
import { FadeIn, StaggeredGrid } from "@/components/animated";
import { motion } from "framer-motion";
import {
  HeroSection,
  TrustBadges,
  AboutSection,
  StatisticsSection,
  FeaturesSection,
  HowItWorksSection,
  TestimonialsSection,
  GuaranteesSection,
  FAQSection,
  FinalCTASection,
} from "@/components/home";
import { TrainingAreasSection } from "@/components/home/TrainingAreasSection";
import { BundlesSection } from "@/components/home/BundlesSection";
import { MaterialsSection } from "@/components/home/MaterialsSection";
import { normalizeImageUrl } from "@/lib/imageUrl";

export default function HomePage() {
  const { data: coursesData, isLoading } = useCourses({ limit: 6 });

  return (
    <div className="overflow-hidden">
      {/* ========== HERO SECTION ========== */}
      <HeroSection />

      {/* ========== TRUST BADGES ========== */}
      <TrustBadges />

      {/* ========== TRAINING AREAS (3 Áreas de Formación) ========== */}
      <TrainingAreasSection />

      {/* ========== ABOUT SECTION ========== */}
      <AboutSection />

      {/* ========== STATISTICS ========== */}
      <StatisticsSection />

      {/* ========== FEATURES ========== */}
      <FeaturesSection />

      {/* ========== HOW IT WORKS ========== */}
      <HowItWorksSection />

      {/* ========== FEATURED COURSES ========== */}
      <section className="py-20 bg-secondary-50/50">
        <div className="container">
          <FadeIn>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
              <div>
                <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 text-sm font-medium rounded-full mb-4">
                  Cursos populares
                </span>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary-900">
                  Cursos que transforman carreras
                </h2>
                <p className="text-secondary-500 mt-3 max-w-xl">
                  Cada curso incluye videos, lecciones y certificado privado al
                  completarlo. Haz clic para ver el contenido y a qué tendrás
                  acceso.
                </p>
              </div>
              <Link to="/courses">
                <Button variant="ghost" className="text-primary-600">
                  Ver todos los cursos
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </FadeIn>

          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="animate-pulse overflow-hidden">
                  <div className="aspect-video bg-secondary-200" />
                  <CardContent className="p-5">
                    <div className="h-6 bg-secondary-200 rounded mb-3 w-3/4" />
                    <div className="h-4 bg-secondary-200 rounded w-full mb-2" />
                    <div className="h-4 bg-secondary-200 rounded w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <StaggeredGrid
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
              staggerDelay={0.1}
            >
              {coursesData?.items.map((course) => (
                <Link key={course.id} to={`/courses/${course.slug}`}>
                  <motion.div
                    whileHover={{ y: -8, transition: { duration: 0.2 } }}
                    className="h-full"
                  >
                    <Card className="overflow-hidden h-full border-0 shadow-md hover:shadow-premium transition-all duration-300 bg-white">
                      <div className="aspect-video relative bg-secondary-100 overflow-hidden">
                        {course.thumbnailUrl ? (
                          <img
                            src={normalizeImageUrl(course.thumbnailUrl) ?? ""}
                            alt={course.title}
                            className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full bg-gradient-to-br from-primary-100 to-primary-200">
                            <BookOpen className="h-16 w-16 text-primary-400" />
                          </div>
                        )}
                        {course.isFree && (
                          <Badge className="absolute top-3 right-3 bg-success-500 hover:bg-success-600 text-white border-0 shadow-md">
                            Gratis
                          </Badge>
                        )}
                        {!course.isFree &&
                          course.hasDiscount &&
                          course.listPrice != null &&
                          course.salePrice != null &&
                          Number(course.listPrice) >
                            Number(course.salePrice) && (
                            <Badge className="absolute top-3 right-3 bg-red-500 hover:bg-red-600 text-white border-0 shadow-md">
                              {Math.round(
                                ((Number(course.listPrice) -
                                  Number(course.salePrice)) /
                                  Number(course.listPrice)) *
                                  100,
                              )}
                              % OFF
                            </Badge>
                          )}
                        {/* Play overlay on hover */}
                        <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                          <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                            <Play className="h-6 w-6 text-primary-600 ml-1" />
                          </div>
                        </div>
                      </div>
                      <CardContent className="p-5">
                        <h3 className="font-semibold text-lg text-secondary-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {course.title}
                        </h3>
                        <p className="text-sm text-secondary-500 mb-4 line-clamp-2">
                          {course.description}
                        </p>
                        <div className="flex items-center justify-between pt-4 border-t border-secondary-100">
                          <div className="flex items-center gap-2">
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className="h-3.5 w-3.5 text-gold-400 fill-gold-400"
                                />
                              ))}
                            </div>
                            <span className="text-xs text-secondary-400">
                              4.9
                            </span>
                          </div>
                          <span className="flex items-baseline gap-2">
                            {!course.isFree &&
                              course.hasDiscount &&
                              course.listPrice != null &&
                              Number(course.listPrice) >
                                Number(course.salePrice ?? 0) && (
                                <span className="text-sm text-secondary-400 line-through">
                                  {formatCurrency(
                                    Number(course.listPrice),
                                    course.currency,
                                  )}
                                </span>
                              )}
                            <span className="font-bold text-lg text-secondary-900">
                              {course.isFree
                                ? "Gratis"
                                : formatCurrency(
                                    course.salePrice ?? 0,
                                    course.currency,
                                  )}
                            </span>
                          </span>
                        </div>
                        {course.level && (
                          <Badge
                            variant="outline"
                            className="mt-3 text-xs border-secondary-200 text-secondary-500"
                          >
                            {COURSE_LEVEL_LABELS[course.level] ?? course.level}
                          </Badge>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                </Link>
              ))}
            </StaggeredGrid>
          )}
        </div>
      </section>

      {/* ========== BUNDLES (Paquetes de Cursos) ========== */}
      <BundlesSection />

      {/* ========== MATERIALES DIGITALES ========== */}
      <MaterialsSection />

      {/* ========== TESTIMONIALS ========== */}
      <TestimonialsSection />

      {/* ========== GUARANTEES ========== */}
      <GuaranteesSection />

      {/* ========== FAQ ========== */}
      <FAQSection />

      {/* ========== FINAL CTA ========== */}
      <FinalCTASection />
    </div>
  );
}
